import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Table, Spinner, Form, Pagination, Alert } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp } from 'react-bootstrap-icons';
import { useSupabase } from '../supabaseAuth';
import SuspensionCheck from '../components/common/SuspensionCheck';

// Actions
import { fetchTransactions, setFilters, setPage, setLimit } from '../store/slices/transactionSlice';

const Transactions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getAccessToken, isAuthenticated, supabase } = useSupabase();

  const { transactions = [], loading, pagination, filters, error } = useSelector((state) => state.transactions);
  const [sortField, setSortField] = React.useState('created_at');
  const [sortDirection, setSortDirection] = React.useState('desc');
  const [authReady, setAuthReady] = React.useState(false);

  // Ensure authentication is ready before making API calls
  useEffect(() => {
    const ensureAuth = async () => {
      try {
        // Check if we already have a token in localStorage
        const existingToken = localStorage.getItem('sb-access-token');
        if (existingToken) {
          // console.log('Token already exists in localStorage');
          setAuthReady(true);
          return;
        }

        // If authenticated but no token in localStorage, get and store it
        if (isAuthenticated) {
          const token = await getAccessToken();
          if (token) {
            localStorage.setItem('sb-access-token', token);
            // console.log('Token fetched and stored in localStorage');
            setAuthReady(true);
            return;
          }
        }

        // Last resort: try to refresh the session directly
        const { data } = await supabase.auth.getSession();
        if (data?.session?.access_token) {
          localStorage.setItem('sb-access-token', data.session.access_token);
          // console.log('Token refreshed and stored in localStorage');
          setAuthReady(true);
          return;
        }

        // If we still don't have a token but we're in development, proceed anyway
        if (process.env.NODE_ENV === 'development') {
          // console.log('Development mode: proceeding without token');
          setAuthReady(true);
          return;
        }

        // console.warn('Could not establish authentication');
        setAuthReady(true); // Proceed anyway, the API call will fail with 401
      } catch (error) {
        console.error('Error ensuring authentication:', error);
        setAuthReady(true); // Proceed anyway, the API call will fail with 401
      }
    };

    ensureAuth();
  }, [isAuthenticated, getAccessToken, supabase]);

  // Load transactions once authentication is ready
  React.useEffect(() => {
    if (authReady) {
      // console.log('Authentication ready, fetching transactions');
      dispatch(fetchTransactions({
        page: pagination.page,
        limit: 10, // Always use 10 as the default limit
        ...filters
      }));
    }
  }, [dispatch, pagination.page, filters, authReady]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date to show full UTC date and time (hour:minute)
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    
    // Format: YYYY-MM-DD HH:MM UTC
    return date.toISOString().replace('T', ' ').substring(0, 16) + ' UTC';
  };

  // Format metal weight based on metal type and weight type
  const formatMetalWeight = (weight, metalType, weightType) => {
    if (!weight) return 'N/A';
    
    // If we have a weight_type from the transaction, use it directly
    if (weightType) {
      // Format weight to show decimals only when needed
      const formattedWeight = weight % 1 === 0 ? weight.toString() : weight.toFixed(4).replace(/\.?0+$/, '');
      return `${formattedWeight} ${weightType}`;
    }
    
    // Legacy: Convert from grams to appropriate unit (for old transactions without weight_type)
    switch (metalType?.toLowerCase()) {
      case 'gold':
      case 'silver':
      case 'platinum':
        // Convert grams to troy ounces (1 troy oz = 31.1035 g)
        const oz = weight / 31.1035;
        return `${oz.toFixed(4)} oz`;
      default:
        return `${weight.toFixed(4)} g`;
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    dispatch(setFilters({ [name]: value }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    dispatch(setPage(page));
  };

  // Sort transactions locally - with safety checks
  const sortedTransactions = React.useMemo(() => {
    if (!Array.isArray(transactions)) {
      console.error('Expected transactions to be an array but got:', typeof transactions);
      return [];
    }
    
    return [...transactions].sort((a, b) => {
      let comparison = 0;

      try {
        switch (sortField) {
          case 'created_at':
            comparison = new Date(a.created_at || 0) - new Date(b.created_at || 0);
            break;
          case 'amount':
            comparison = (a.amount || 0) - (b.amount || 0);
            break;
          case 'transaction_type':
            comparison = (a.transaction_type || '').localeCompare(b.transaction_type || '');
            break;
          case 'status':
            comparison = (a.status || '').localeCompare(b.status || '');
            break;
          case 'subscription_name':
            comparison = (a.subscription_name || '').localeCompare(b.subscription_name || '');
            break;
          default:
            comparison = 0;
        }
      } catch (err) {
        console.error('Error sorting transactions:', err);
        return 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [transactions, sortField, sortDirection]);

  // Generate pagination items
  const totalPages = Math.ceil((pagination?.total || 0) / 10) || 1;
  const paginationItems = [];
  
  // Show at most 5 pages in pagination
  const startPage = Math.max(1, (pagination?.page || 1) - 2);
  const endPage = Math.min(totalPages, startPage + 4);
  
  for (let number = startPage; number <= endPage; number++) {
    paginationItems.push(
      <Pagination.Item
        key={number}
        active={number === (pagination?.page || 1)}
        onClick={() => handlePageChange(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  // If still waiting for authentication, show loading spinner
  if (!authReady) {
    return (
      <Container fluid className="py-4">
        <div className="d-flex justify-content-center my-5">
          <Spinner animation="border" variant="primary" />
          <span className="ms-2">Preparing authentication...</span>
        </div>
      </Container>
    );
  }

  return (
    <SuspensionCheck>
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2 className="fw-bold">Transactions</h2>
            <p className="text-muted">View and manage your transaction history</p>
          </Col>
        </Row>

        {loading ? (
          <div className="d-flex justify-content-center my-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            <Card className="shadow-sm mb-4">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3 mb-md-0">
                      <Form.Label>Filter by Status</Form.Label>
                      <Form.Select
                        name="status"
                        value={filters?.status || ''}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Statuses</option>
                        <option value="completed">Paid/Completed</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3 mb-md-0">
                      <Form.Label>Filter by Type</Form.Label>
                      <Form.Select
                        name="type"
                        value={filters?.type || ''}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Types</option>
                        <option value="payment">Payment</option>
                        <option value="delivery">Delivery</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {error && (
              <Alert variant="danger" className="mb-4">
                {typeof error === 'string' ? error : (typeof error === 'object' && error.description ? error.description : 'Error loading transactions')}
                {error === 'Failed to fetch transactions' && (
                  <div className="mt-2">
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => {
                        // Force token refresh and retry
                        supabase.auth.refreshSession().then(() => {
                          dispatch(fetchTransactions({
                            page: pagination.page,
                            limit: 10, // Always use 10 as the default limit
                            ...filters
                          }));
                        });
                      }}
                    >
                      Refresh Authentication & Retry
                    </Button>
                  </div>
                )}
              </Alert>
            )}

            <Card className="shadow-sm">
              <Card.Body className="p-0">
                {Array.isArray(sortedTransactions) && sortedTransactions.length > 0 ? (
                  <Table responsive hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th
                          className="cursor-pointer"
                          onClick={() => handleSort('created_at')}
                        >
                          Date & Time (UTC)
                          {sortField === 'created_at' && (
                            sortDirection === 'asc' ? <ArrowUp className="ms-1" /> : <ArrowDown className="ms-1" />
                          )}
                        </th>
                        <th
                          className="cursor-pointer"
                          onClick={() => handleSort('subscription_name')}
                        >
                          Subscription
                          {sortField === 'subscription_name' && (
                            sortDirection === 'asc' ? <ArrowUp className="ms-1" /> : <ArrowDown className="ms-1" />
                          )}
                        </th>
                        <th
                          className="cursor-pointer"
                          onClick={() => handleSort('transaction_type')}
                        >
                          Type
                          {sortField === 'transaction_type' && (
                            sortDirection === 'asc' ? <ArrowUp className="ms-1" /> : <ArrowDown className="ms-1" />
                          )}
                        </th>
                        <th>Description</th>
                        <th
                          className="cursor-pointer"
                          onClick={() => handleSort('amount')}
                        >
                          Amount
                          {sortField === 'amount' && (
                            sortDirection === 'asc' ? <ArrowUp className="ms-1" /> : <ArrowDown className="ms-1" />
                          )}
                        </th>
                        <th>Metal Amount</th>
                        <th
                          className="cursor-pointer"
                          onClick={() => handleSort('status')}
                        >
                          Status
                          {sortField === 'status' && (
                            sortDirection === 'asc' ? <ArrowUp className="ms-1" /> : <ArrowDown className="ms-1" />
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTransactions.map((transaction) => (
                        <tr key={transaction.id || Math.random()}>
                          <td>{formatDateTime(transaction.created_at)}</td>
                          <td>{transaction.subscription_name || 'N/A'}</td>
                          <td className="text-capitalize">{transaction.transaction_type || 'N/A'}</td>
                          <td>{transaction.description || 'N/A'}</td>
                          <td>{transaction.amount ? formatCurrency(transaction.amount) : 'N/A'}</td>
                          <td>{transaction.metal_amount ? formatMetalWeight(transaction.metal_amount, transaction.metal_type, transaction.weight_type) : 'N/A'}</td>
                          <td>
                            {transaction.status && (
                              <Badge
                                bg={
                                  transaction.transaction_type === 'delivery' && transaction.status === 'completed' ? 'primary' :
                                  transaction.status === 'completed' ? 'success' :
                                  transaction.status === 'pending' ? 'warning' :
                                  'danger'
                                }
                                pill
                              >
                                {transaction.transaction_type === 'payment' && transaction.status === 'completed' 
                                  ? 'paid' 
                                  : transaction.transaction_type === 'delivery' && transaction.status === 'completed'
                                  ? 'shipped'
                                  : transaction.status}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i className="bi bi-receipt fs-1 text-muted"></i>
                    </div>
                    <h5>No transactions found</h5>
                    <p className="text-muted">
                      {filters?.status || filters?.type ?
                        'Try changing your filter criteria' :
                        'Your transaction history will appear here'}
                    </p>
                  </div>
                )}
              </Card.Body>

              {Array.isArray(sortedTransactions) && sortedTransactions.length > 0 && (
                <Card.Footer className="bg-white d-flex justify-content-between align-items-center">
                  <div>
                    Showing {Math.min(10, sortedTransactions.length)} of {pagination?.total || 0} transactions
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.First onClick={() => handlePageChange(1)} disabled={(pagination?.page || 1) === 1} />
                    <Pagination.Prev onClick={() => handlePageChange((pagination?.page || 1) - 1)} disabled={(pagination?.page || 1) === 1} />
                    {paginationItems}
                    <Pagination.Next onClick={() => handlePageChange((pagination?.page || 1) + 1)} disabled={(pagination?.page || 1) === totalPages} />
                    <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={(pagination?.page || 1) === totalPages} />
                  </Pagination>
                </Card.Footer>
              )}
            </Card>

            <div className="mt-4 text-center">
              <Button
                variant="outline-primary"
                onClick={() => {
                  // Reset filters
                  dispatch(setFilters({
                    status: '',
                    type: '',
                    subscriptionId: null
                  }));
                  dispatch(setPage(1));
                  // Refresh data
                  dispatch(fetchTransactions({
                    limit: 10 // Always use 10 as the default limit
                  }));
                }}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                Refresh Transactions
              </Button>
            </div>
          </>
        )}
      </Container>
    </SuspensionCheck>
  );
};

export default Transactions;

